const LV_ISTANBUL_TIME_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Istanbul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const LV_TURKISH_DATE_FORMATTER = new Intl.DateTimeFormat('tr-TR', {
  timeZone: 'UTC',
  day: 'numeric',
  month: 'long',
});

const LV_SAME_DAY_CUTOFF_MINUTES = 15 * 60;
const LV_DEFAULT_DELIVERY_PREFERENCE = 'Hızlı gönderim planı';
const LV_COURIER_DELIVERY_PREFERENCE = 'İstanbul içi kurye · Uygunluk teyidi';

// Full-day and half-day official holidays are both excluded from dispatch promises.
// Moving religious holidays are sourced from the Presidency of Religious Affairs calendar.
const LV_TURKEY_FIXED_HOLIDAYS = new Set([
  '01-01',
  '04-23',
  '05-01',
  '05-19',
  '07-15',
  '08-30',
  '10-28',
  '10-29',
]);

const LV_TURKEY_MOVING_HOLIDAYS = new Set([
  '2026-03-19', '2026-03-20', '2026-03-21', '2026-03-22',
  '2026-05-26', '2026-05-27', '2026-05-28', '2026-05-29', '2026-05-30',
  '2027-03-08', '2027-03-09', '2027-03-10', '2027-03-11',
  '2027-05-15', '2027-05-16', '2027-05-17', '2027-05-18', '2027-05-19',
  '2028-02-25', '2028-02-26', '2028-02-27', '2028-02-28',
  '2028-05-04', '2028-05-05', '2028-05-06', '2028-05-07', '2028-05-08',
  '2029-02-13', '2029-02-14', '2029-02-15', '2029-02-16',
  '2029-04-23', '2029-04-24', '2029-04-25', '2029-04-26', '2029-04-27',
  '2030-02-03', '2030-02-04', '2030-02-05', '2030-02-06',
  '2030-04-12', '2030-04-13', '2030-04-14', '2030-04-15', '2030-04-16',
]);

function createDateOnly(year, month, day) {
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
}

function getDateKey(date) {
  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, '0'),
    String(date.getUTCDate()).padStart(2, '0'),
  ].join('-');
}

function getIstanbulNow(now = new Date()) {
  const parts = Object.fromEntries(
    LV_ISTANBUL_TIME_FORMATTER.formatToParts(now).map(({ type, value }) => [type, value]),
  );

  return {
    date: createDateOnly(parts.year, parts.month, parts.day),
    minutes: Number(parts.hour) * 60 + Number(parts.minute),
  };
}

function isTurkeyOfficialHoliday(date) {
  const key = getDateKey(date);
  return LV_TURKEY_FIXED_HOLIDAYS.has(key.slice(5)) || LV_TURKEY_MOVING_HOLIDAYS.has(key);
}

function isOfficialBusinessDay(date) {
  const weekday = date.getUTCDay();
  return weekday !== 0 && weekday !== 6 && !isTurkeyOfficialHoliday(date);
}

function addCalendarDays(date, amount) {
  const result = new Date(date.getTime());
  result.setUTCDate(result.getUTCDate() + amount);
  return result;
}

function nextOfficialBusinessDay(date) {
  let candidate = addCalendarDays(date, 1);
  while (!isOfficialBusinessDay(candidate)) candidate = addCalendarDays(candidate, 1);
  return candidate;
}

function addOfficialBusinessDays(date, amount) {
  let result = new Date(date.getTime());
  let remaining = amount;

  while (remaining > 0) {
    result = addCalendarDays(result, 1);
    if (isOfficialBusinessDay(result)) remaining -= 1;
  }

  return result;
}

function formatDate(date) {
  return LV_TURKISH_DATE_FORMATTER.format(date);
}

function formatDateRange(start, end) {
  if (start.getUTCFullYear() === end.getUTCFullYear() && start.getUTCMonth() === end.getUTCMonth()) {
    return `${start.getUTCDate()}–${formatDate(end)}`;
  }

  return `${formatDate(start)}–${formatDate(end)}`;
}

function getDeliveryJourney(now = new Date()) {
  const istanbulNow = getIstanbulNow(now);
  const todayIsBusinessDay = isOfficialBusinessDay(istanbulNow.date);
  const shipsToday = todayIsBusinessDay && istanbulNow.minutes < LV_SAME_DAY_CUTOFF_MINUTES;
  const dispatchDate = shipsToday ? istanbulNow.date : nextOfficialBusinessDay(istanbulNow.date);

  let message = 'Şimdi sipariş verirsen sonraki iş günü kargoda.';
  let dayKind = 'after-cutoff';

  if (shipsToday) {
    message = 'Saat 15.00’e kadar sipariş ver, aynı gün kargoya verelim.';
    dayKind = 'same-day';
  } else if (!todayIsBusinessDay) {
    message = 'Şimdi sipariş verirsen ilk iş günü kargoda.';
    dayKind = 'non-business-day';
  }

  return {
    dayKind,
    message,
    orderDate: istanbulNow.date,
    dispatchDate,
    arrivalStart: addOfficialBusinessDays(dispatchDate, 1),
    arrivalEnd: addOfficialBusinessDays(dispatchDate, 2),
  };
}

function getIstanbulDispatchMessage(now = new Date()) {
  return getDeliveryJourney(now).message;
}

function updatePurchaseDispatch(root = document) {
  let journey;

  try {
    journey = getDeliveryJourney();
  } catch (_error) {
    return;
  }

  root.querySelectorAll('[data-lv-purchase-dispatch]').forEach((element) => {
    const output = element.querySelector('[data-lv-purchase-dispatch-text]');
    if (!output) return;

    output.textContent = journey.message;
    element.dataset.lvDispatchReady = 'true';
  });

  root.querySelectorAll('[data-lv-delivery-journey]').forEach((element) => {
    const message = element.querySelector('[data-lv-delivery-journey-message]');
    const orderDate = element.querySelector('[data-lv-delivery-order-date]');
    const dispatchDate = element.querySelector('[data-lv-delivery-dispatch-date]');
    const arrivalDate = element.querySelector('[data-lv-delivery-arrival-date]');

    if (message) message.textContent = journey.message;
    if (orderDate) {
      orderDate.textContent = formatDate(journey.orderDate);
      orderDate.dateTime = getDateKey(journey.orderDate);
    }
    if (dispatchDate) {
      dispatchDate.textContent = formatDate(journey.dispatchDate);
      dispatchDate.dateTime = getDateKey(journey.dispatchDate);
    }
    if (arrivalDate) {
      arrivalDate.textContent = formatDateRange(journey.arrivalStart, journey.arrivalEnd);
      arrivalDate.dateTime = `${getDateKey(journey.arrivalStart)}/${getDateKey(journey.arrivalEnd)}`;
    }

    element.dataset.lvDeliveryDayKind = journey.dayKind;
    element.dataset.lvDeliveryReady = 'true';
    element.setAttribute(
      'aria-label',
      `${journey.message} Sipariş ${formatDate(journey.orderDate)}, kargoya veriliş ${formatDate(journey.dispatchDate)}, tahmini teslimat ${formatDateRange(journey.arrivalStart, journey.arrivalEnd)}.`,
    );
  });
}

function getPreferenceScope(group) {
  return group.closest('.product-information') || document;
}

function updateStickyDeliverySummary(group, preference) {
  const scope = getPreferenceScope(group);
  const dispatch = scope.querySelector('[data-lv-sticky-delivery-dispatch]');
  const summary = scope.querySelector('[data-lv-sticky-delivery-summary]');
  const deliveryCard = scope.querySelector('[data-lv-delivery-summary-card]');
  const cardEyebrow = deliveryCard?.querySelector('[data-lv-delivery-card-eyebrow]');
  const cardMessage = deliveryCard?.querySelector('[data-lv-delivery-card-message]');
  const cardStatus = deliveryCard?.querySelector('[data-lv-delivery-card-status]');

  if (dispatch && !dispatch.dataset.lvStandardDeliveryMessage) {
    dispatch.dataset.lvStandardDeliveryMessage = dispatch.textContent.trim();
  }

  if (summary && !summary.dataset.lvStandardDeliverySummary) {
    summary.dataset.lvStandardDeliverySummary = summary.textContent.trim();
  }

  if (deliveryCard && !deliveryCard.dataset.lvStandardDeliveryLabel) {
    deliveryCard.dataset.lvStandardDeliveryLabel = deliveryCard.getAttribute('aria-label') || '';
  }
  if (cardEyebrow && !cardEyebrow.dataset.lvStandardDeliveryEyebrow) {
    cardEyebrow.dataset.lvStandardDeliveryEyebrow = cardEyebrow.textContent.trim();
  }
  if (cardMessage && !cardMessage.dataset.lvStandardDeliveryMessage) {
    cardMessage.dataset.lvStandardDeliveryMessage = cardMessage.textContent.trim();
  }
  if (cardStatus && !cardStatus.dataset.lvStandardDeliveryStatus) {
    cardStatus.dataset.lvStandardDeliveryStatus = cardStatus.textContent.trim();
  }

  if (preference === LV_COURIER_DELIVERY_PREFERENCE) {
    if (dispatch) dispatch.textContent = 'İstanbul içi kurye · Uygunluğu teyit et';
    if (summary) summary.textContent = 'Teyit sonrası · 2–3 saatte kapında';
    if (deliveryCard) {
      deliveryCard.classList.add('is-courier');
      deliveryCard.setAttribute('aria-label', 'İstanbul içi kurye; bölge ve saat uygunluğu teyit edildiğinde tahmini 2–3 saat içinde kapında');
    }
    if (cardEyebrow) cardEyebrow.textContent = 'İstanbul içi kurye';
    if (cardMessage) cardMessage.textContent = 'Teyit sonrası 2–3 saat içinde kapında';
    if (cardStatus) cardStatus.textContent = 'WhatsApp’tan teyit al';
    return;
  }

  if (dispatch?.dataset.lvStandardDeliveryMessage) {
    dispatch.textContent = dispatch.dataset.lvStandardDeliveryMessage;
  }
  if (summary?.dataset.lvStandardDeliverySummary) {
    summary.textContent = summary.dataset.lvStandardDeliverySummary;
  }
  if (deliveryCard) {
    deliveryCard.classList.remove('is-courier');
    deliveryCard.setAttribute('aria-label', deliveryCard.dataset.lvStandardDeliveryLabel);
  }
  if (cardEyebrow?.dataset.lvStandardDeliveryEyebrow) {
    cardEyebrow.textContent = cardEyebrow.dataset.lvStandardDeliveryEyebrow;
  }
  if (cardMessage?.dataset.lvStandardDeliveryMessage) {
    cardMessage.textContent = cardMessage.dataset.lvStandardDeliveryMessage;
  }
  if (cardStatus?.dataset.lvStandardDeliveryStatus) {
    cardStatus.textContent = cardStatus.dataset.lvStandardDeliveryStatus;
  }
}

function applyDeliveryPreference(group, preference, emitChange = false) {
  const choices = [...group.querySelectorAll('[data-lv-delivery-choice]')];
  const selectedChoice = choices.find((choice) => choice.value === preference)
    || choices.find((choice) => choice.hasAttribute('data-lv-delivery-default'));
  const selectedPreference = selectedChoice?.value || LV_DEFAULT_DELIVERY_PREFERENCE;

  choices.forEach((choice) => {
    const selected = choice === selectedChoice;
    choice.checked = selected;
    const option = choice.closest('.lv-delivery-choice-v86');
    option?.classList.toggle('is-selected', selected);
    const state = option?.querySelector('[data-lv-delivery-state]');
    if (state) state.textContent = selected ? 'Seçili' : 'Seç';
  });

  const preferenceInput = getPreferenceScope(group).querySelector('[data-lv-delivery-preference-input]');
  if (preferenceInput) preferenceInput.value = selectedPreference;
  group.dataset.lvDeliveryPreference = selectedPreference;
  updateStickyDeliverySummary(group, selectedPreference);

  if (emitChange) {
    group.dispatchEvent(new CustomEvent('velora:delivery-preference-change', {
      bubbles: true,
      detail: { preference: selectedPreference },
    }));
  }
}

function initializeDeliveryPreferences(root = document) {
  root.querySelectorAll('[data-lv-delivery-preference]').forEach((group) => {
    if (group.dataset.lvDeliveryReady !== 'true') {
      const defaultChoice = group.querySelector('[data-lv-delivery-default]');
      if (defaultChoice) defaultChoice.checked = true;

      group.addEventListener('change', (event) => {
        const choice = event.target.closest?.('[data-lv-delivery-choice]');
        if (!choice || !group.contains(choice)) return;
        applyDeliveryPreference(group, choice.value, true);
      });
      group.dataset.lvDeliveryReady = 'true';
    }

    const selectedChoice = group.querySelector('[data-lv-delivery-choice]:checked');
    applyDeliveryPreference(group, selectedChoice?.value || LV_DEFAULT_DELIVERY_PREFERENCE);
  });
}

if (typeof document !== 'undefined') {
  updatePurchaseDispatch();
  initializeDeliveryPreferences();

  document.addEventListener('shopify:section:load', (event) => {
    updatePurchaseDispatch(event.target);
    initializeDeliveryPreferences(event.target);
  });

  document.addEventListener('variant:update', () => {
    updatePurchaseDispatch();
    initializeDeliveryPreferences();
  });

  window.addEventListener('pageshow', () => updatePurchaseDispatch());
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') updatePurchaseDispatch();
  });
}

export { getDateKey, getDeliveryJourney, isOfficialBusinessDay };
